import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useProjectTemplates } from '../../../../../hooks/ReactQueryHooks/useProjectTemplateQuery';
import { projectsSelector } from '../../../../../store';
import { setProjectTemplate } from '../../../../../store/Projects/Project/ProjectTemplate';
import { formatDate3 } from '../../../../../utils';
import Button from '../../../../UI/Button';
import styles from '../Form.module.css';
import { normalizeProjectTemplateCard, type ProjectTemplateCardInput } from './utils';

interface ProjectTemplateInfoProps {
  handleProgress: (type: string) => void;
}

const ProjectTemplateInfo = ({ handleProgress }: ProjectTemplateInfoProps) => {
  const dispatch = useDispatch();
  const [params, setParams] = useState<any>({
    perPage: 10,
    page: 1,
  });

  const fetchProjectTemplates = useProjectTemplates({
    params,
  });
  const projectTemplates = (fetchProjectTemplates?.data?.data ?? []) as ProjectTemplateCardInput[];
  const projectTemplatesCount = fetchProjectTemplates?.data?.meta?.total ?? 0;
  const pageCount = Math.ceil(projectTemplatesCount / params.perPage);
  const { Project } = useSelector(projectsSelector);
  const selectedProjectTemplate = Project.ProjectTemplate;

  const handleTemplateSelect = (template: any) => {
    dispatch(
      setProjectTemplate({
        id: template.id,
        name: template.name,
      })
    );
  };

  const backHandler = () => {
    handleProgress('BACK');
  };

  const forwardHandler = () => {
    handleProgress('FORWARD');
  };

  const projectTemplatesPageHandler = (type: 'PREV' | 'NEXT') => {
    if (type === 'NEXT' && pageCount !== params.page) {
      setParams((prevState: { perPage: number; page: number }) => ({
        ...prevState,
        page: prevState.page + 1,
      }));
    }

    if (type === 'PREV' && params.page !== 1) {
      setParams((prevState: { perPage: number; page: number }) => ({
        ...prevState,
        page: prevState.page - 1,
      }));
    }
  };

  return (
    <>
      <h2 className={`mb-0 ${styles['form-title']}`}>Project Template</h2>
      <div className='grid grid-cols-12 gap-2 mt-5'>
        {projectTemplates.map((template) => {
          const temp = normalizeProjectTemplateCard(template);
          const isSelected = temp.id === selectedProjectTemplate?.id;

          return (
            <div
              key={temp.id ?? temp.name}
              className={`col-span-6 p-4 rounded cursor-pointer hover:bg-green-50 relative ${
                isSelected ? 'bg-green-50' : 'bg-neutralColor-lightest'
              }`}
              onClick={() => handleTemplateSelect(temp)}
            >
              <h3 className='font-bold truncate'>{temp.name}</h3>
              {isSelected && (
                <span className='text-primaryColor-normal absolute top-2 right-2'>
                  <CheckIcon className='h-5 w-5' />
                </span>
              )}
              <p className='truncate'>{temp.description}</p>
              <p className='italic text-xs'>Created by {temp.creatorName}</p>
              <p className='italic text-xs'>Last updated on {temp.updatedAt ? formatDate3(temp.updatedAt) : 'none'}</p>
            </div>
          );
        })}
      </div>
      {pageCount > 1 && (
        <div className='w-full flex gap-2 justify-end items-center mt-2'>
          <span
            className={`flex justify-center items-center cursor-pointer text-white p-1 rounded ${
              params.page === 1 ? 'bg-neutralColor-light' : 'bg-primaryColor-normal'
            }`}
            onClick={() => projectTemplatesPageHandler('PREV')}
          >
            <ChevronLeftIcon className='h-5 w-5' />
          </span>
          <span
            className={`flex justify-center items-center cursor-pointer text-white  p-1 rounded ${
              params.page === pageCount ? 'bg-neutralColor-light' : 'bg-primaryColor-normal'
            }`}
            onClick={() => projectTemplatesPageHandler('NEXT')}
          >
            <ChevronRightIcon className='h-5 w-5' />
          </span>
        </div>
      )}
      <div className='w-full flex justify-end gap-4 mt-5'>
        <Button type='button' btnStyle='btn-neutral' label='Previous' onClick={backHandler} />
        <Button type='button' btnStyle='btn-primary' label='Next' onClick={forwardHandler} />
      </div>
    </>
  );
};

export default ProjectTemplateInfo;
